import { createUIResource } from '@mcp-ui/server';
import { createLogger, summarizePayload } from '../../logging/index.js';
import { estimateTokens, formatTokenCount } from '../../services/token.service.js';
import { checkStoreModelCompatibility } from '../../utils/model-validation.js';
import { LRUCache } from '../cache.js';
import { SearchArgsSchema, GetFullContextArgsSchema } from '../schemas/index.js';
import { renderSearchResults } from '../ui/search-results.js';
import type { SearchQuery, DocumentId, StoreId } from '../../types/index.js';
import type { SearchResult } from '../../types/search.js';
import type { Store } from '../../types/store.js';
import type { SearchArgs, GetFullContextArgs } from '../schemas/index.js';
import type { ToolHandler, ToolResponse } from '../types.js';

const logger = createLogger('mcp-search');

// Create result cache for get_full_context
// Uses LRU cache to prevent memory leaks (max 1000 items)
export const resultCache = new LRUCache<DocumentId, SearchResult>(1000);

/**
 * Handle search requests
 *
 * Searches across specified stores (or all stores if none specified) using
 * hybrid vector + FTS search. Results are cached for get_full_context retrieval.
 */
export const handleSearch: ToolHandler<SearchArgs> = async (
  args,
  context
): Promise<ToolResponse> => {
  // Validate arguments with Zod
  let validated = SearchArgsSchema.parse(args);

  // find-files intent: force minimal detail and bump default limit for compact file discovery
  if (validated.intent === 'find-files') {
    validated = {
      ...validated,
      detail: 'minimal',
      ...(validated.limit === 10 ? { limit: 20 } : {}),
    };
  }

  logger.info(
    {
      query: validated.query,
      stores: validated.stores,
      mode: validated.mode,
      detail: validated.detail,
      limit: validated.limit,
      intent: validated.intent,
    },
    'Search started'
  );

  const { services } = context;

  // Get all stores if none specified, resolve store names to IDs
  const stores: Store[] =
    validated.stores !== undefined
      ? await Promise.all(
          validated.stores.map(async (s) => {
            // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
            const store = await services.store.getByIdOrName(s as StoreId);
            if (!store) {
              throw new Error(`Store not found: ${s}`);
            }
            return store;
          })
        )
      : await services.store.list();

  // Filter stores by model compatibility (skip incompatible instead of failing)
  const currentModelId = services.store.getCurrentModelId();
  const compatibleStores: Store[] = [];
  const skippedStores: string[] = [];

  for (const store of stores) {
    const check = checkStoreModelCompatibility(store, { currentModelId });
    if (check.compatible) {
      compatibleStores.push(store);
    } else {
      skippedStores.push(`${store.name}: ${check.reason ?? 'incompatible'}`);
    }
  }

  // Fail if no compatible stores found
  if (compatibleStores.length === 0) {
    const allV1 = skippedStores.length > 0 && skippedStores.every((s) => s.includes('schema v1'));
    const hint = allV1 ? ' Run: /bluera-knowledge:index <store> to upgrade.' : '';
    throw new Error(
      `No compatible stores found.${skippedStores.length > 0 ? ` Skipped: ${skippedStores.join('; ')}` : ''}${hint}`
    );
  }

  // Log skipped stores as warning
  if (skippedStores.length > 0) {
    logger.warn({ skippedStores }, 'Some stores skipped due to model incompatibility');
  }

  const storeIds = compatibleStores.map((s) => s.id);

  // Initialize stores with error handling
  try {
    services.lance.setDimensions(await services.embeddings.ensureDimensions());
    for (const storeId of storeIds) {
      await services.lance.initialize(storeId);
    }
  } catch (error) {
    throw new Error(
      `Failed to initialize vector stores: ${error instanceof Error ? error.message : String(error)}`
    );
  }

  // Perform search
  const searchQuery: SearchQuery = {
    query: validated.query,
    stores: storeIds,
    mode: validated.mode,
    limit: validated.limit,
    detail: validated.detail,
    threshold: validated.threshold,
    minRelevance: validated.minRelevance,
    intent: validated.intent,
  };

  const results = await services.search.search(searchQuery);

  // Cache results for get_full_context (with LRU eviction)
  for (const result of results.results) {
    resultCache.set(result.id, result);
  }

  // Batch store lookup (dedup by storeId) — avoids per-result getByIdOrName calls
  const storeById = new Map<string, Store>();
  const uniqueStoreIds = new Set(results.results.map((r) => r.metadata.storeId));
  for (const storeId of uniqueStoreIds) {
    const store = await services.store.getByIdOrName(storeId);
    if (store) storeById.set(storeId, store);
  }

  // Build store map for response (store name → path/url for follow-up reads)
  const storeMap: Record<string, { type: string; path?: string; url?: string }> = {};
  for (const store of storeById.values()) {
    storeMap[store.name] = {
      type: store.type,
      ...('path' in store ? { path: store.path } : {}),
      ...('url' in store && store.url !== undefined ? { url: store.url } : {}),
    };
  }

  // Add repoRoot to results for cloned repos
  const enhancedResults = results.results.map((r) => {
    const store = storeById.get(r.metadata.storeId);

    return {
      id: r.id,
      score: r.score,
      summary: {
        ...r.summary,
        storeName: store?.name,
        repoRoot: store?.type === 'repo' ? store.path : undefined,
      },
      context: r.context,
      full: r.full,
    };
  });

  // find-files intent: return compact file listing instead of full results
  if (validated.intent === 'find-files') {
    const seen = new Set<string>();
    const files = enhancedResults
      .filter((r) => {
        const loc = r.summary.location?.split(':')[0] ?? '';
        if (seen.has(loc)) return false;
        seen.add(loc);
        return true;
      })
      .map((r) => ({
        location: r.summary.location,
        name: r.summary.name,
        type: r.summary.type,
        score: r.score,
      }));

    const findFilesData = {
      files,
      stores: storeMap,
      totalResults: files.length,
      timeMs: results.timeMs,
    };
    const findFilesJson = JSON.stringify(findFilesData, null, 2);
    const findFilesTokens = estimateTokens(findFilesJson);
    const findFilesHeader = `Find files: "${validated.query}" | Files: ${String(files.length)} | ${formatTokenCount(findFilesTokens)} tokens | ${String(results.timeMs)}ms\n\n`;

    logger.info(
      {
        query: validated.query,
        totalFiles: files.length,
        responseTokens: findFilesTokens,
        timeMs: results.timeMs,
      },
      'Find-files search complete'
    );

    return {
      content: [{ type: 'text', text: findFilesHeader + findFilesJson }],
    };
  }

  const responseData: Record<string, unknown> = {
    stores: storeMap,
    results: enhancedResults,
    totalResults: results.totalResults,
    mode: results.mode,
    timeMs: results.timeMs,
  };
  if (results.confidence !== undefined) {
    responseData['confidence'] = results.confidence;
  }
  if (results.maxRawScore !== undefined) {
    responseData['maxRawScore'] = results.maxRawScore;
  }
  if (results.rerankTimeMs !== undefined) {
    responseData['rerankTimeMs'] = results.rerankTimeMs;
  }
  const responseJson = JSON.stringify(responseData, null, 2);

  // Calculate actual token estimate based on response content
  const responseTokens = estimateTokens(responseJson);

  // Create visible header with token usage and confidence
  const confidenceInfo =
    results.confidence !== undefined ? ` | Confidence: ${results.confidence}` : '';
  const rerankInfo =
    results.rerankTimeMs !== undefined ? ` | Rerank: ${String(results.rerankTimeMs)}ms` : '';
  const header = `Search: "${validated.query}" | Results: ${String(results.totalResults)} | ${formatTokenCount(responseTokens)} tokens | ${String(results.timeMs)}ms${confidenceInfo}${rerankInfo}\n\n`;

  // Log the complete MCP response that will be sent to Claude Code
  logger.info(
    {
      query: validated.query,
      totalResults: results.totalResults,
      responseTokens,
      timeMs: results.timeMs,
      ...summarizePayload(responseJson, 'mcp-response', validated.query),
    },
    'Search complete - context sent to Claude Code'
  );

  const textContent = { type: 'text' as const, text: header + responseJson };

  // UIResource type:'resource' is accepted by MCP SDK at runtime but not in its TS content union.
  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call
  const uiResource = createUIResource({
    uri: `ui://bk/search/${encodeURIComponent(validated.query)}`,
    content: {
      type: 'rawHtml',
      htmlString: renderSearchResults(validated.query, enhancedResults, {
        timeMs: results.timeMs,
        confidence: results.confidence,
        mode: results.mode,
        stores: storeMap,
      }),
    },
    encoding: 'text',
  });

  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
  const content = [textContent, uiResource];

  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
  return { content };
};

/**
 * Handle get_full_context requests
 *
 * Retrieves full context for a previously cached search result (by resultId),
 * or retrieves indexed context for a file by path (by file).
 */
export const handleGetFullContext: ToolHandler<GetFullContextArgs> = async (
  args,
  context
): Promise<ToolResponse> => {
  // Validate arguments with Zod
  const validated = GetFullContextArgsSchema.parse(args);

  const { services } = context;

  // File-based retrieval: search by file path using FTS
  if (validated.file !== undefined) {
    const filePath = validated.file;
    logger.info({ file: filePath, store: validated.store }, 'Get full context by file path');

    // Resolve target stores
    let targetStores: Store[];
    if (validated.store !== undefined) {
      const store = await services.store.getByIdOrName(validated.store);
      if (!store) throw new Error(`Store not found: ${validated.store}`);
      targetStores = [store];
    } else {
      targetStores = await services.store.list();
    }

    const currentModelId = services.store.getCurrentModelId();
    const compatible = targetStores.filter(
      (s) => checkStoreModelCompatibility(s, { currentModelId }).compatible
    );

    if (compatible.length === 0) {
      throw new Error('No compatible stores found');
    }

    services.lance.setDimensions(await services.embeddings.ensureDimensions());
    for (const s of compatible) {
      await services.lance.initialize(s.id);
    }

    const fileResults = await services.search.search({
      query: filePath,
      stores: compatible.map((s) => s.id),
      mode: 'fts',
      limit: 5,
      detail: 'full',
    });

    // Filter to results whose path contains the requested file
    const matching = fileResults.results.filter(
      (r) => r.metadata.path?.includes(filePath) === true
    );

    if (matching.length === 0) {
      throw new Error(`No indexed content found for file: ${filePath}`);
    }

    // Cache for future resultId-based access
    for (const r of matching) {
      resultCache.set(r.id, r);
    }

    const responseJson = JSON.stringify(
      {
        results: matching.map((r) => ({
          id: r.id,
          score: r.score,
          summary: r.summary,
          context: r.context,
          full: r.full,
        })),
      },
      null,
      2
    );

    logger.info(
      {
        file: filePath,
        resultCount: matching.length,
        ...summarizePayload(responseJson, 'mcp-full-context-file', filePath),
      },
      'Full context retrieved by file path'
    );

    return {
      content: [{ type: 'text', text: responseJson }],
    };
  }

  // ResultId-based retrieval (existing path)
  logger.info({ resultId: validated.resultId }, 'Get full context requested');

  // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
  const resultId = validated.resultId as DocumentId;

  // Check cache for result
  const cachedResult = resultCache.get(resultId);

  if (!cachedResult) {
    throw new Error(`Result not found in cache: ${resultId}. Run a search first to cache results.`);
  }

  // If result already has full context, return it
  if (cachedResult.full) {
    const responseJson = JSON.stringify(
      {
        id: cachedResult.id,
        score: cachedResult.score,
        summary: cachedResult.summary,
        context: cachedResult.context,
        full: cachedResult.full,
      },
      null,
      2
    );

    logger.info(
      {
        resultId,
        cached: true,
        hasFullContext: true,
        ...summarizePayload(responseJson, 'mcp-full-context', resultId),
      },
      'Full context retrieved from cache'
    );

    return {
      content: [
        {
          type: 'text',
          text: responseJson,
        },
      ],
    };
  }

  // Otherwise, re-query with full detail
  const store = await services.store.getByIdOrName(cachedResult.metadata.storeId);

  if (!store) {
    throw new Error(`Store not found: ${cachedResult.metadata.storeId}`);
  }

  services.lance.setDimensions(await services.embeddings.ensureDimensions());
  await services.lance.initialize(store.id);

  const searchQuery: SearchQuery = {
    query: cachedResult.content.substring(0, 100), // Use snippet of content as query
    stores: [store.id],
    mode: 'hybrid',
    limit: 1,
    detail: 'full',
  };

  const results = await services.search.search(searchQuery);

  // Find matching result by ID
  const fullResult = results.results.find((r) => r.id === resultId);

  if (!fullResult) {
    // Return cached result even if we couldn't get full detail
    return {
      content: [
        {
          type: 'text',
          text: JSON.stringify(
            {
              id: cachedResult.id,
              score: cachedResult.score,
              summary: cachedResult.summary,
              context: cachedResult.context,
              warning: 'Could not retrieve full context, returning cached minimal result',
            },
            null,
            2
          ),
        },
      ],
    };
  }

  // Update cache with full result
  resultCache.set(resultId, fullResult);

  const responseJson = JSON.stringify(
    {
      id: fullResult.id,
      score: fullResult.score,
      summary: fullResult.summary,
      context: fullResult.context,
      full: fullResult.full,
    },
    null,
    2
  );

  logger.info(
    {
      resultId,
      cached: false,
      hasFullContext: true,
      ...summarizePayload(responseJson, 'mcp-full-context', resultId),
    },
    'Full context retrieved via re-query'
  );

  return {
    content: [
      {
        type: 'text',
        text: responseJson,
      },
    ],
  };
};
